import { jsonOr, type DumbStorageSerializer } from "@/utils/dumb-local-storage";
import type { Logger } from "@/utils/logger";
import { SimpleObservableValue } from "@/utils/observable";
import { ObservableDumbStorageValue } from "@/utils/observable-dumb-local-storage";
import * as z from "zod/v4-mini";

export enum GqlAuthChannelType {
  otp = "otp",
  oauth = "oauth",
}

export const sdkAuthUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  username: z.string(),
});

export const sdkValidatedUserIdentifierSchema = z.object({
  type: z.string(),
  value: z.string(),
  confirmation_token: z.string(),
});

export const sdkAuthObjectSchema = z.object({
  authToken: z.string(),
  user: z.nullable(sdkAuthUserSchema),
  validatedIdentifiers: z.array(sdkValidatedUserIdentifierSchema),
  authChannelType: z.enum(GqlAuthChannelType),
});

export type SDKAuthUser = z.infer<typeof sdkAuthUserSchema>;
export type SDKAuthObject = z.infer<typeof sdkAuthObjectSchema>;

export class SDKAuthState {
  #logger: Logger;
  public value: ObservableDumbStorageValue<SDKAuthObject | null>;
  public isAuthenticating = new SimpleObservableValue<boolean>(false);

  #dumbStorageSerializer: DumbStorageSerializer<SDKAuthObject | null> = {
    read: (v) => {
      const value = jsonOr(v, null);
      if (!value) return null;

      this.#logger.debug("Reading auth token", {
        value,
      });

      const result = sdkAuthObjectSchema.safeParse(value);
      if (!result.success) {
        this.#logger.error("Failed to parse auth token", {
          error: result.error,
        });
        return null;
      }

      this.#logger.debug("Parsed auth token", {
        result,
      });

      return result.data;
    },
    write: (value) => {
      this.#logger.debug("Writing auth token", {
        value,
      });
      if (!value) return null;
      return JSON.stringify(value);
    },
  };

  constructor(logger: Logger) {
    this.#logger = logger.extend(this.constructor.name);
    this.value = new ObservableDumbStorageValue("fanfest-auth-token", null, {
      options: {
        serializer: this.#dumbStorageSerializer,
      },
      storage: localStorage,
    });
  }

  get authToken(): string | null {
    return this.value.get()?.authToken ?? null;
  }

  get user(): SDKAuthUser | null {
    return this.value.get()?.user ?? null;
  }

  get isAuthenticated(): boolean {
    this.#logger.debug("Checking if authenticated", {
      value: this.value.get(),
    });
    return this.value.get() !== null;
  }

  get hasUser(): boolean {
    return this.user !== null;
  }
}
